// src/pages/EditClient.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Container,
  Toolbar,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  Divider,
  Stack,
  Chip,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import PersonIcon from "@mui/icons-material/Person";
import { doc, getDoc, updateDoc, Timestamp } from "firebase/firestore";
import { db } from "../contexts/firebase/config";

const PLANOS = [
  { value: "Base", label: "Base (1 ano)" },
  { value: "Flex", label: "Flex (1 ano)" },
  { value: "PRO", label: "PRO (3 meses)" },
  { value: "Expirado", label: "Expirado" },
];

const MODALIDADES = [
  { value: "Anual", label: "Anual" },
  { value: "Trimestral", label: "Trimestral" },
  { value: "Expirado", label: "Expirado" },
];

const ESTADOS = [
  { value: "Novo", label: "Novo" },
  { value: "Renovação", label: "Renovação" },
  { value: "Expirado", label: "Expirado" },
];

export default function EditClient() {
  const { clienteId } = useParams();
  const navigate = useNavigate();

  const [fields, setFields] = useState({
    nome: "",
    email: "",
    nif: "",
    morada: "",
    contacto: "",
    plano: "Base",
    modalidade: "Anual",
    estado: "Novo",
    dataRegisto: "",
  });

  const [empresaId, setEmpresaId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchClient() {
      setLoading(true);
      setError("");

      try {
        const ref = doc(db, "clientes", clienteId);
        const snap = await getDoc(ref);

        if (!snap.exists()) {
          setError("Cliente não encontrado.");
        } else {
          const data = snap.data();

          setEmpresaId(data.empresaId || "");

          setFields({
            nome: data.nome || "",
            email: data.email || "",
            nif: data.nif || "",
            morada: data.morada || "",
            contacto: data.contacto || "",
            plano: data.plano || "Base",
            modalidade: data.modalidade || "Anual",
            estado: data.estado || "Novo",
            dataRegisto: data.dataRegisto
              ? data.dataRegisto.toDate().toISOString().slice(0, 10)
              : "",
          });
        }
      } catch (err) {
        console.error(err);
        setError("Falha ao carregar dados do cliente.");
      }

      setLoading(false);
    }

    fetchClient();
  }, [clienteId]);

  const handleChange = (e) => {
  const { name, value } = e.target;

  if (name === "plano" && value === "Expirado") {
    setFields((f) => ({
      ...f,
      plano: "Expirado",
      modalidade: "Expirado",
      estado: "Expirado",
    }));
    return;
  }

  setFields((f) => ({ ...f, [name]: value }));
};

  const getEstadoColor = (estado) => {
  if (estado === "Expirado") return "error";
  if (estado === "Renovação") return "warning";
  return "success";
};
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!fields.nome.trim()) {
      setError("O nome é obrigatório.");
      return;
    }

    setSaving(true);

    try {
      const ref = doc(db, "clientes", clienteId);

      const updates = {
        nome: fields.nome.trim(),
        email: fields.email.trim() || null,
        nif: fields.nif.trim() || null,
        morada: fields.morada.trim() || null,
        contacto: fields.contacto.trim() || null,
        plano: fields.plano,
        modalidade: fields.modalidade,
        estado: fields.estado,
      };

      if (fields.dataRegisto) {
        updates.dataRegisto = Timestamp.fromDate(new Date(fields.dataRegisto));
      }

      await updateDoc(ref, updates);
      navigate(`/admin/empresa/${empresaId}/clientes`);
    } catch (err) {
      console.error(err);
      setError("Falha ao guardar alterações.");
    }

    setSaving(false);
  };

  if (loading) {
    return (
      <Container sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          A carregar dados do cliente...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Toolbar disableGutters sx={{ mb: 2 }}>
        <Button
          component={RouterLink}
          to={`/admin/empresa/${empresaId}/clientes`}
          startIcon={<ArrowBackIcon />}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Voltar à lista
        </Button>
      </Toolbar>

      <Card
        elevation={0}
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
        }}
      >
        <Box
          sx={{
            p: { xs: 3, sm: 4 },
            background:
              "linear-gradient(135deg, rgba(249,115,22,0.12), rgba(15,23,42,0.04))",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: 3,
                    display: "grid",
                    placeItems: "center",
                    bgcolor: "primary.main",
                    color: "primary.contrastText",
                  }}
                >
                  <PersonIcon />
                </Box>

                <Box>
                  <Typography variant="h4" fontWeight={900}>
                    Editar Cliente
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Atualiza os dados do cliente e guarda as alterações.
                  </Typography>
                </Box>
              </Stack>
            </Box>

            <Chip
              label={fields.estado}
              color={getEstadoColor(fields.estado)}
              sx={{
                fontWeight: 800,
                px: 1,
                borderRadius: 2,
              }}
            />
          </Stack>
        </Box>

        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
              }}
            >
              <Typography variant="h6" fontWeight={900} sx={{ mb: 0.5 }}>
                Dados do cliente
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Informação principal associada ao cliente.
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <TextField
                  label="Nome"
                  name="nome"
                  value={fields.nome}
                  onChange={handleChange}
                  required
                  fullWidth
                />

                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={fields.email}
                  onChange={handleChange}
                  fullWidth
                />

                <TextField
                  label="NIF"
                  name="nif"
                  value={fields.nif}
                  onChange={handleChange}
                  fullWidth
                />

                <TextField
                  label="Contacto"
                  name="contacto"
                  value={fields.contacto}
                  onChange={handleChange}
                  fullWidth
                />

                <TextField
                  label="Morada"
                  name="morada"
                  value={fields.morada}
                  onChange={handleChange}
                  fullWidth
                  sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}
                />
              </Box>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: { xs: 2, sm: 3 },
                borderRadius: 4,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.default",
              }}
            >
              <Typography variant="h6" fontWeight={900} sx={{ mb: 0.5 }}>
                Plano e estado
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Define o plano, modalidade e estado atual do cliente.
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  gap: 2,
                }}
              >
                <FormControl fullWidth required>
                  <InputLabel>Plano</InputLabel>
                  <Select
                    name="plano"
                    value={fields.plano}
                    label="Plano"
                    onChange={handleChange}
                  >
                    {PLANOS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth required>
                  <InputLabel>Modalidade</InputLabel>
                  <Select
                    name="modalidade"
                    value={fields.modalidade}
                    label="Modalidade"
                    onChange={handleChange}
                  >
                    {MODALIDADES.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth required>
                  <InputLabel>Estado</InputLabel>
                  <Select
                    name="estado"
                    value={fields.estado}
                    label="Estado"
                    onChange={handleChange}
                  >
                    {ESTADOS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  label="Data de Registo"
                  name="dataRegisto"
                  type="date"
                  value={fields.dataRegisto}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Box>
            </Paper>

            <Divider sx={{ my: 3 }} />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              justifyContent="flex-end"
            >
              <Button
                component={RouterLink}
                to={`/admin/empresa/${empresaId}/clientes`}
                variant="outlined"
                sx={{
                  py: 1.3,
                  px: 4,
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 800,
                }}
              >
                Cancelar
              </Button>

              <Button
                type="submit"
                variant="contained"
                startIcon={saving ? null : <SaveIcon />}
                disabled={saving}
                sx={{
                  py: 1.3,
                  px: 5,
                  borderRadius: 3,
                  textTransform: "none",
                  fontWeight: 900,
                  boxShadow: "0 10px 24px rgba(249, 115, 22, 0.28)",
                }}
              >
                {saving ? "A guardar..." : "Guardar alterações"}
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}